import React, { useState, useEffect, useCallback } from 'react';
import { FileDown, RefreshCw } from 'lucide-react';
import { LoadingSpinner } from 'components/common';
import NotificationToast from 'components/common/NotificationToast';
import AnaliseRelatorios from './relatorios/AnaliseRelatorios';
import ContasPagasDia from './relatorios/ContasPagasDia';
import ModalGerandoPdf from './relatorios/ModalGerandoPdf';
import ModalPreviewRelatorioPdf from './relatorios/ModalPreviewRelatorioPdf';
import FiltroData from './contasReceber/components/FiltroData';
import api from 'services/api';
import { usePermissions } from 'hooks/usePermissions';

const Relatorios = () => {
  const { hasPermission } = usePermissions();
  const canExportRelatorios = hasPermission('administrativo.relatorios.exportar');
  const [loading, setLoading] = useState(true);
  const [dados, setDados] = useState([]);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);
  const [relatorioAtivo, setRelatorioAtivo] = useState('geral');
  const [gerandoPdf, setGerandoPdf] = useState(false);
  const [previewPdf, setPreviewPdf] = useState({
    open: false,
    dataUrl: '',
    fileName: ''
  });

  const relatoriosDisponiveis = [
    { id: 'geral', label: 'Relatório geral de contas', enabled: true },
    { id: 'fechamento_caixa', label: 'Relatório de contas pagas no dia', enabled: true },
    { id: 'fluxo_caixa', label: 'Fluxo de caixa (em breve)', enabled: false },
    { id: 'receitas_despesas', label: 'Receitas x despesas (em breve)', enabled: false }
  ];

  // Calcular datas padrão: hoje e 30 dias atrás
  const getDataPadrao = () => {
    const hoje = new Date();
    const trintaDiasAtras = new Date();
    trintaDiasAtras.setDate(hoje.getDate() - 30);

    const pad = (n) => String(n).padStart(2, '0');
    const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

    return {
      dataFim: toISO(hoje),
      dataInicio: toISO(trintaDiasAtras)
    };
  };

  const [range, setRange] = useState(() => {
    const padrao = getDataPadrao();
    return {
      presetId: 'mes_atual',
      start: padrao.dataInicio,
      end: padrao.dataFim
    };
  });
  const dataInicio = range.start;
  const dataFim = range.end;

  const carregarDados = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post('/n8n/administracao/relatorios/historico-contas-bancarias', {
        data_inicio: dataInicio,
        data_fim: dataFim
      });

      if (response.data.success) {
        const dadosBrutos = response.data.data || [];
        const dadosProcessados = processarDadosRelatorios(dadosBrutos);
        setDados(dadosProcessados);
      } else {
        setDados([]);
        setError(response.data.message || 'Erro ao carregar dados');
      }
    } catch (error) {
      console.error('Erro ao carregar dados para relatórios:', error);
      setDados([]);
      setError(error.response?.data?.message || 'Erro ao carregar dados. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [dataInicio, dataFim]);

  // Função para processar dados conforme a estrutura da API de relatórios
  const processarDadosRelatorios = (dadosBrutos) => {
    return dadosBrutos.map(item => {
      let historico = item.historico || [];
      const conta = item.conta || {};
      const saldoInicial = parseFloat(item.saldo_inicial || 0);

      historico = [...historico].sort((a, b) => {
        const dataA = new Date(a.data || 0);
        const dataB = new Date(b.data || 0);
        return dataA - dataB;
      });

      let saldoAnterior = saldoInicial;
      historico = historico.map((transacao) => {
        const entrada = parseFloat(transacao.entrada || transacao.recebido || 0);
        const saida = parseFloat(transacao.saida || 0);

        const saldoDiario = saldoAnterior + entrada - Math.abs(saida);
        saldoAnterior = saldoDiario;

        return {
          ...transacao,
          saldo_diario: saldoDiario
        };
      });

      const entrada = historico.reduce((sum, h) => {
        const valor = parseFloat(h.recebido || h.entrada || 0);
        return sum + valor;
      }, 0);

      const saida = historico.reduce((sum, h) => {
        return sum + parseFloat(h.saida || 0);
      }, 0);

      const saldo = saldoInicial + entrada - Math.abs(saida);
      const nomeConta = conta.nome || conta.descricao || conta.conta || `Conta ${conta.id || conta.ID || 'N/A'}`;

      return {
        id: conta.id || conta.ID || nomeConta,
        conta: nomeConta,
        conta_bancaria: nomeConta,
        saldo: saldo,
        saldo_inicial: saldoInicial,
        valor_total: saldo,
        entrada: entrada,
        valor_pago: entrada,
        saida: saida,
        valor_aberto: saida,
        historico: historico,
        dadosConta: conta,
        totalRegistros: historico.length,
        fornecedor: conta.fornecedor || 'N/A',
        categoria: conta.categoria || 'N/A',
        status: 'ativo',
        vencida: false,
        proxima_vencimento: false
      };
    });
  };

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  useEffect(() => {
    const handleCompanyChange = () => {
      carregarDados();
    };

    window.addEventListener('company-context-changed', handleCompanyChange);
    window.addEventListener('company-changed', handleCompanyChange);

    return () => {
      window.removeEventListener('company-context-changed', handleCompanyChange);
      window.removeEventListener('company-changed', handleCompanyChange);
    };
  }, [carregarDados]);

  const handleExportarPdf = async () => {
    if (!canExportRelatorios) {
      setNotification({ type: 'error', message: 'Você não tem permissão para exportar relatórios.' });
      return;
    }

    if (gerandoPdf) return;
    if (!['geral', 'fechamento_caixa'].includes(relatorioAtivo)) return;

    setGerandoPdf(true);
    try {
      const pad = (n) => String(n).padStart(2, '0');
      const hoje = new Date();
      const hojeISO = `${hoje.getFullYear()}-${pad(hoje.getMonth() + 1)}-${pad(hoje.getDate())}`;
      const reportType = relatorioAtivo === 'fechamento_caixa' ? 'contas_pagas_dia' : relatorioAtivo;

      const response = await api.post('/n8n/administracao/relatorios/exportar-pdf', {
        relatorio: reportType,
        data_inicio: reportType === 'contas_pagas_dia' ? hojeISO : dataInicio,
        data_fim: reportType === 'contas_pagas_dia' ? hojeISO : dataFim
      }, {
        timeout: 300000
      });

      const payload = response?.data?.data;
      if (!response?.data?.success || !payload?.pdf_base64 || !payload?.mime_type) {
        throw new Error(response?.data?.message || 'Resposta inválida da API de exportação');
      }

      const dataUrl = `data:${payload.mime_type};base64,${payload.pdf_base64}`;
      setPreviewPdf({
        open: true,
        dataUrl,
        fileName: payload.file_name || 'relatorio.pdf'
      });
    } catch (err) {
      console.error('Erro ao exportar relatório em PDF:', err);
      let message = err?.response?.data?.message || 'Não foi possível exportar o relatório em PDF.';
      if (err?.code === 'ECONNABORTED') {
        message = 'A exportação excedeu o tempo limite. Tente um período menor.';
      }
      if (err?.code === 'ERR_CANCELED') {
        message = 'A requisição de exportação foi cancelada. Tente novamente.';
      }
      setNotification({ type: 'error', message });
    } finally {
      setGerandoPdf(false);
    }
  };

  if (loading && dados.length === 0) {
    return (
      <div className="relative min-h-[360px]">
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-[color:var(--bg)]/65 backdrop-blur-[1px]">
          <LoadingSpinner
            fullScreen={false}
            className="min-h-[160px] w-full max-w-md"
            cardClassName="w-full"
            label="Carregando relatório"
            subtitle="Aguarde enquanto atualizamos os dados."
          />
        </div>
      </div>
    );
  }

  const renderTabs = () => (
    <div className="app-scroll-subtle overflow-x-auto px-0.5 py-0.5 sm:rounded-[18px] sm:border sm:border-[color:var(--header-control-border-subtle)] sm:bg-[color:var(--surface)]/88 sm:px-2 sm:py-2 sm:shadow-[0_10px_30px_rgba(2,6,23,0.12)]">
      <div className="flex min-w-full w-max items-center gap-1.5">
        {relatoriosDisponiveis.map((rel) => {
          const isActive = relatorioAtivo === rel.id;
          return (
            <button
              key={rel.id}
              type="button"
              onClick={() => {
                if (!rel.enabled) return;
                setRelatorioAtivo(rel.id);
              }}
              disabled={!rel.enabled}
              aria-disabled={!rel.enabled}
              className={`group relative flex min-h-[44px] shrink-0 items-center gap-2.5 rounded-[14px] border border-transparent px-4 py-2.5 text-sm transition-[background-color,color,border-color,box-shadow,transform] duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/40 focus-visible:ring-offset-0 ${
                isActive
                  ? 'bg-transparent text-[color:var(--text-primary)] md:border-[color:var(--header-control-border-subtle)] md:bg-[color:var(--surface-muted)]/55 md:shadow-[inset_0_1px_0_rgba(255,255,255,0.04),0_0_0_1px_rgba(168,85,247,0.12)]'
                  : 'text-[color:var(--text-muted)] hover:bg-[color:var(--surface-muted)]/20 hover:text-[color:var(--text-primary)] md:hover:border-[color:var(--header-control-border-subtle)] md:hover:bg-[color:var(--surface-muted)]/38'
              } ${rel.enabled ? '' : 'cursor-not-allowed opacity-60'}`}
            >
              <span className={`whitespace-nowrap ${isActive ? 'font-semibold text-[color:var(--text-primary)]' : 'font-medium'}`}>
                {rel.label}
              </span>
              <span
                aria-hidden="true"
                className={`absolute inset-x-3 bottom-1 h-0.5 rounded-full transition-opacity duration-200 ${
                  isActive ? 'bg-[color:var(--accent)] opacity-100' : 'opacity-0'
                }`}
              />
            </button>
          );
        })}
      </div>
    </div>
  );

  if (error && dados.length === 0) {
    return (
      <div className="overflow-hidden rounded-lg border border-[color:var(--border)] bg-[color:var(--surface)]">
        <div className="border-b border-[color:var(--border)] px-4 py-3">
          {renderTabs()}
        </div>
        <div className="p-4">
          <div className="rounded-lg border border-[color:var(--border)] bg-[color:var(--surface-muted)] p-4">
            <p className="text-[color:var(--status-bad)]">{error}</p>
            <button
              onClick={carregarDados}
              className="mt-2 inline-flex h-10 items-center rounded-lg border border-[color:var(--border)] bg-[color:var(--surface)] px-4 text-sm font-medium text-[color:var(--status-bad)] transition-colors hover:bg-[color:var(--surface-muted)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/40"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {relatorioAtivo === 'geral' ? (
        <div className="bg-transparent rounded-lg overflow-visible sm:bg-[color:var(--surface)] sm:border sm:border-[color:var(--border)]">
          <div className="px-0 py-2 sm:border-b sm:border-[color:var(--border)] sm:px-4 sm:py-3">
            {renderTabs()}
          </div>

          <div className="px-1 py-1.5 sm:border-b sm:border-[color:var(--border)] sm:bg-[color:var(--surface-muted)] sm:px-4 sm:py-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="text-sm font-medium text-[color:var(--text-primary)]">Relatório geral de contas</div>
              <div className="flex flex-wrap items-center gap-2 sm:ml-auto sm:justify-end">
                <FiltroData value={range} loading={loading} onChange={setRange} />
                <button
                  type="button"
                  onClick={handleExportarPdf}
                  disabled={loading || gerandoPdf || !canExportRelatorios}
                  className="inline-flex h-10 items-center gap-2 rounded-lg bg-[color:var(--accent)] px-3 text-sm text-[color:var(--avatar-text)] transition-colors hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/40"
                  title={canExportRelatorios ? 'Exportar PDF' : 'Sem permissão para exportar'}
                >
                  <FileDown className="h-4 w-4" />
                  {gerandoPdf ? 'Gerando...' : 'Exportar PDF'}
                </button>
                <button
                  type="button"
                  onClick={carregarDados}
                  className="rounded-md p-2 text-[color:var(--text-muted)] transition-colors hover:bg-[color:var(--surface)] hover:text-[color:var(--text-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/40"
                  title="Atualizar"
                  aria-label="Atualizar relatório geral"
                >
                  <RefreshCw className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          <div className="relative pt-2 sm:p-4">
            {loading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-[color:var(--bg)]/65 backdrop-blur-[1px]">
                <LoadingSpinner
                  fullScreen={false}
                  className="min-h-[160px] w-full max-w-md"
                  cardClassName="w-full"
                  label="Carregando relatório"
                  subtitle="Aguarde enquanto atualizamos os dados."
                />
              </div>
            )}

            <AnaliseRelatorios
              dados={dados}
              dataInicio={dataInicio}
              dataFim={dataFim}
              onDataInicioChange={(novaData) => setRange(prev => ({ ...prev, start: novaData, presetId: 'personalizado' }))}
              onDataFimChange={(novaData) => setRange(prev => ({ ...prev, end: novaData, presetId: 'personalizado' }))}
              showHeader={false}
              withContainer={false}
            />
          </div>
        </div>
      ) : (
        <div className="bg-transparent rounded-lg overflow-visible sm:bg-[color:var(--surface)] sm:border sm:border-[color:var(--border)] sm:overflow-hidden">
          <div className="px-0 py-2 sm:border-b sm:border-[color:var(--border)] sm:px-4 sm:py-3">
            {renderTabs()}
          </div>

          <div className="pt-2 sm:p-4">
            {relatorioAtivo === 'fechamento_caixa' ? (
              <ContasPagasDia
                onExportPdf={handleExportarPdf}
                exportingPdf={gerandoPdf}
                canExportPdf={canExportRelatorios}
              />
            ) : (
              <div className="rounded-lg border border-[color:var(--border)] bg-[color:var(--surface)] p-6 text-[color:var(--text-muted)]">
                Este relatório ainda está em desenvolvimento.
              </div>
            )}
          </div>
        </div>
      )}

      {notification && (
        <NotificationToast
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      <ModalGerandoPdf isOpen={gerandoPdf} />
      <ModalPreviewRelatorioPdf
        isOpen={previewPdf.open}
        onClose={() => setPreviewPdf({ open: false, dataUrl: '', fileName: '' })}
        dataUrl={previewPdf.dataUrl}
        fileName={previewPdf.fileName}
      />
    </div>
  );
};

export default Relatorios;
